import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './AuthContext';
import { useAuth } from './AuthContext';

const TUTORIAL_DONE_KEY = 'feedback_tutorial_done';

export function shouldShowFeedbackTutorial() {
  return !localStorage.getItem(TUTORIAL_DONE_KEY);
}

interface Step {
  id: string;
  title: string;
  body: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  target?: string;
  waitForAction?: boolean;
  remeasureDelay?: number;
  onEnter?: () => void;
  actionLabel?: string;
  pollFor?: string;
}

const STEPS: Step[] = [
  {
    id: 'welcome',
    placement: 'center',
    title: '💬 Help & Feedback',
    body: "This is the Help & Feedback page. You can report issues, send suggestions, and chat with the admin team directly. Tap anywhere to continue.",
    actionLabel: "Let's go →",
  },
  {
    id: 'sidebar-tickets',
    target: '.feedback-layout .psb-sidebar',
    placement: 'right',
    title: '📋 Your Tickets Sidebar',
    body: "Your submitted tickets appear here. You can click any ticket to open a live chat with the admin team. Tap anywhere to continue.",
    remeasureDelay: 200,
  },
  {
    id: 'feedback-form',
    target: '.feedback-form',
    placement: 'left',
    title: '📝 Submit Feedback',
    body: "Choose a category — Help or Feedback — then describe your issue. The admin team will reply directly. Tap anywhere to continue.",
    remeasureDelay: 200,
  },
  {
    id: 'select-category',
    target: '.feedback-form',
    placement: 'left',
    title: '🆘 Try It — Select a Category',
    body: "Select either 'Help' or 'Feedback' to continue the tutorial.",
    waitForAction: true,
    remeasureDelay: 200,
  },
  {
    id: 'type-message',
    target: '.feedback-form textarea',
    placement: 'left',
    title: '✏️ Type Your Message',
    body: "Now type something in the message box — anything works for this demo. Then click Send.",
    waitForAction: true,
    remeasureDelay: 200,
  },
  {
    id: 'open-ticket-chat',
    target: '.feedback-layout .psb-sidebar .sidebar-section',
    placement: 'right',
    title: '💬 Your Ticket is Here!',
    body: "Your ticket is now in the sidebar. Click the 💬 button on it to open the chat with the admin team.",
    waitForAction: true,
    remeasureDelay: 600,
  },
  {
    id: 'admin-greeting',
    target: '.chat-messages',
    placement: 'left',
    title: '� Admin says hi!',
    body: "The admin team has sent you a message. Tap anywhere to read it and reply.",
    remeasureDelay: 800,
  },
  {
    id: 'send-message',
    target: '.chat-input-row',
    placement: 'top',
    title: '✉️ Reply to Admin',
    body: "Type a reply and press ↑ or Enter to send it.",
    waitForAction: true,
    remeasureDelay: 300,
  },
  {
    id: 'admin-replied',
    target: '.chat-messages',
    placement: 'left',
    title: '🤖 Admin Replied Again',
    body: "The admin team replied to your message! In real use, a human admin will respond here. Tap anywhere to continue.",
    remeasureDelay: 500,
  },
  {
    id: 'done',
    placement: 'center',
    title: '🎉 All Done!',
    body: "You now know how the feedback system works. Use it anytime you need help or want to suggest improvements. Happy trading! 🏝️",
    actionLabel: 'Got it →',
  },
];

const PAD = 10;
const TOOLTIP_H_EST = 240;

interface SpotlightRect { top: number; left: number; width: number; height: number; }

function getRect(selector: string): SpotlightRect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

interface FeedbackTutorialProps {
  onComplete: () => void;
  fakeChatTicketId: string | null;
  onOpenFakeChat: () => void;
  onRefreshTickets: () => void;
}

export default function FeedbackTutorial({ onComplete, fakeChatTicketId, onOpenFakeChat, onRefreshTickets }: FeedbackTutorialProps) {
  const { user } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [visible, setVisible] = useState(false);
  const measureTimers = useRef<number[]>([]);
  const adminReplyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fakeTicketId = useRef<string | null>(fakeChatTicketId);

  useEffect(() => { fakeTicketId.current = fakeChatTicketId; }, [fakeChatTicketId]);

  const step = STEPS[stepIdx];

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  const measureTarget = useCallback((targetOverride?: string) => {
    const target = targetOverride || step.target;
    if (!target) { setRect(null); return; }
    measureTimers.current.forEach(clearTimeout);
    measureTimers.current = [];
    let attempts = 0;
    const tryMeasure = () => {
      const el = document.querySelector(target);
      if (el) {
        const t = window.setTimeout(() => {
          const r = getRect(target);
          if (r) setRect(r);
          else if (attempts < 12) { attempts++; measureTimers.current.push(window.setTimeout(tryMeasure, 100)); }
          else setRect(null);
        }, 80);
        measureTimers.current.push(t);
      } else if (attempts < 12) {
        attempts++;
        measureTimers.current.push(window.setTimeout(tryMeasure, 100));
      } else setRect(null);
    };
    tryMeasure();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.body.setAttribute('data-tutorial-step', step.id);
    return () => document.body.removeAttribute('data-tutorial-step');
  }, [step.id]);

  useEffect(() => {
    setVisible(false);
    setRect(null);
    if (step.onEnter) step.onEnter();
    const delay = step.remeasureDelay ? step.remeasureDelay + 100 : 300;
    const t = window.setTimeout(() => { measureTarget(); setVisible(true); }, delay);
    return () => clearTimeout(t);
  }, [stepIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = () => { if (step.target) measureTarget(); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [measureTarget, step.target]);

  // Poll for waitForAction steps
  useEffect(() => {
    if (!step.waitForAction) return;

    if (step.id === 'select-category') {
      // Advance when a category button shows active styling (border changes via React state)
      const interval = window.setInterval(() => {
        const form = document.querySelector('.feedback-form');
        if (!form) return;
        const buttons = Array.from(form.querySelectorAll('button[type="button"]')) as HTMLElement[];
        const selected = buttons.some(b => {
          const s = b.style.background || '';
          return s.includes('rgba(74') || s.includes('rgba(167');
        });
        if (selected) { window.clearInterval(interval); setTimeout(() => setStepIdx(i => i + 1), 300); }
      }, 300);
      return () => window.clearInterval(interval);
    }

    if (step.id === 'type-message') {
      // Listen for the form submit event directly
      const form = document.querySelector('.feedback-form') as HTMLFormElement | null;
      if (!form) return;
      const handleSubmit = () => {
        onRefreshTickets();
        setTimeout(() => setStepIdx(i => i + 1), 900);
      };
      form.addEventListener('submit', handleSubmit);
      return () => form.removeEventListener('submit', handleSubmit);
    }

    if (step.id === 'open-ticket-chat') {
      // When chat modal opens, send admin greeting then advance
      const interval = window.setInterval(() => {
        if (document.querySelector('.chat-modal')) {
          window.clearInterval(interval);
          if (fakeTicketId.current) {
            supabase.from('admin_messages').insert({
              report_id: fakeTicketId.current,
              sender_id: 'abf65bea-b257-405b-a297-c9a58d90549a',
              content: "Hey! 👋 Thanks for reaching out. I can see your message — how can I help you today?",
            }).then(({ error }) => {
              if (error) console.error('Admin greeting insert error:', error);
              setTimeout(() => setStepIdx(i => i + 1), 900);
            });
          } else {
            setTimeout(() => setStepIdx(i => i + 1), 600);
          }
        }
      }, 300);
      return () => window.clearInterval(interval);
    }

    if (step.id === 'send-message') {
      // Advance when a message is sent — detect new .chat-bubble-wrap.mine
      let initialCount = document.querySelectorAll('.chat-bubble-wrap.mine').length;
      const interval = window.setInterval(() => {
        const current = document.querySelectorAll('.chat-bubble-wrap.mine').length;
        if (current > initialCount) {
          window.clearInterval(interval);
          // Send fake admin reply after 1.5s
          if (fakeTicketId.current) {
            adminReplyTimer.current = setTimeout(async () => {
              const { error } = await supabase.from('admin_messages').insert({
                report_id: fakeTicketId.current,
                sender_id: 'abf65bea-b257-405b-a297-c9a58d90549a',
                content: "Thanks for trying the feedback system! In real use, a human admin will respond here. Feel free to use this anytime you need help. 🏝️",
              });
              if (error) console.error('Admin reply insert error:', error);
              setTimeout(() => setStepIdx(i => i + 1), 800);
            }, 1500);
          } else {
            setTimeout(() => setStepIdx(i => i + 1), 800);
          }
        }
      }, 400);
      return () => { window.clearInterval(interval); if (adminReplyTimer.current) clearTimeout(adminReplyTimer.current); };
    }
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = () => {
    if (stepIdx === STEPS.length - 1) {
      localStorage.setItem(TUTORIAL_DONE_KEY, '1');
      onComplete(); // onComplete in FeedbackPage handles ticket/message cleanup
    } else {
      setStepIdx(i => i + 1);
    }
  };

  const skip = () => {
    localStorage.setItem(TUTORIAL_DONE_KEY, '1');
    onComplete(); // onComplete in FeedbackPage handles ticket/message cleanup
  };

  const getTooltipStyle = (): React.CSSProperties => {
    if (!rect || step.placement === 'center') {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', maxWidth: 'min(92vw,400px)', width: 'min(92vw,400px)' };
    }
    const vw = window.innerWidth, vh = window.innerHeight;
    const TW = Math.min(vw * 0.9, 370);
    const centreX = rect.left + rect.width / 2;
    const clampedLeft = Math.max(8, Math.min(centreX - TW / 2, vw - TW - 8));

    if (step.placement === 'bottom') {
      const top = rect.top + rect.height + PAD + 8;
      if (top + TOOLTIP_H_EST > vh - 8) return { position: 'fixed', bottom: vh - rect.top + PAD + 8, left: clampedLeft, width: TW, maxWidth: TW };
      return { position: 'fixed', top, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (step.placement === 'top') {
      return { position: 'fixed', bottom: vh - rect.top + PAD + 8, left: clampedLeft, width: TW, maxWidth: TW };
    }
    if (step.placement === 'right') {
      const left = Math.min(rect.left + rect.width + PAD + 8, vw - TW - 8);
      const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
      return { position: 'fixed', top, left, width: TW, maxWidth: TW };
    }
    // left
    const left = Math.max(8, rect.left - TW - PAD - 8);
    const top = Math.max(8, Math.min(rect.top + rect.height / 2 - TOOLTIP_H_EST / 2, vh - TOOLTIP_H_EST - 8));
    return { position: 'fixed', top, left, width: TW, maxWidth: TW };
  };

  const spotlightStyle: React.CSSProperties = rect ? {
    position: 'fixed',
    top: rect.top - PAD, left: rect.left - PAD,
    width: rect.width + PAD * 2, height: rect.height + PAD * 2,
    borderRadius: 14,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    border: '1.5px solid rgba(255,140,30,0.7)',
    animation: 'tutorial-glow-pulse 1.8s ease-in-out infinite',
    pointerEvents: 'none', zIndex: 200001,
    transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1), height 0.3s cubic-bezier(0.4,0,0.2,1)',
  } : {
    position: 'fixed', top: 0, left: 0, width: 0, height: 0,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.76)',
    pointerEvents: 'none', zIndex: 200001,
  };

  const renderBlocker = () => {
    const blockEvt = (e: React.MouseEvent | React.TouchEvent) => { e.stopPropagation(); e.preventDefault(); };
    if (!step.waitForAction || !rect) {
      return (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200000 }}
          onClick={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchStart={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchMove={e => e.preventDefault()}
        />
      );
    }
    const vw = window.innerWidth, vh = window.innerHeight;
    const ht = rect.top - PAD, hl = rect.left - PAD;
    const hb = rect.top + rect.height + PAD, hr = rect.left + rect.width + PAD;
    const s: React.CSSProperties = { position: 'fixed', zIndex: 200000, background: 'transparent' };
    return (
      <>
        <div style={{ ...s, top: 0, left: 0, width: vw, height: Math.max(0, ht) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: hb, left: 0, width: vw, height: Math.max(0, vh - hb) }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: 0, width: Math.max(0, hl), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
        <div style={{ ...s, top: ht, left: hr, width: Math.max(0, vw - hr), height: hb - ht }} onClick={blockEvt} onTouchStart={blockEvt} onTouchMove={blockEvt} />
      </>
    );
  };

  return (
    <>
      <div
        className={`tutorial-root${visible ? ' visible' : ''}`}
        data-tutorial-step={step.id}
        style={{ position: 'fixed', inset: 0, zIndex: 200000, pointerEvents: step.waitForAction ? 'none' : 'all' }}
      >
        {renderBlocker()}
        {!rect && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.76)', zIndex: 200001, pointerEvents: 'none' }} />}
        {rect && <div style={spotlightStyle} />}

        <div
          className="tutorial-card"
          style={{ ...getTooltipStyle(), pointerEvents: 'all', zIndex: 200002, transition: 'top 0.3s cubic-bezier(0.4,0,0.2,1), left 0.3s cubic-bezier(0.4,0,0.2,1), bottom 0.3s cubic-bezier(0.4,0,0.2,1)' }}
          onClick={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
          onTouchStart={e => { e.stopPropagation(); if (!step.waitForAction) advance(); }}
        >
          <div className="tutorial-dots">
            {STEPS.map((_, i) => (
              <span key={i} className={`tutorial-dot ${i === stepIdx ? 'active' : i < stepIdx ? 'done' : ''}`} />
            ))}
          </div>

          <h3 className="tutorial-title">{step.title}</h3>
          <p className="tutorial-body" style={{ whiteSpace: 'pre-line' }}>{step.body}</p>

          <div className="tutorial-actions">
            <button className="tutorial-skip" onClick={skip}>Skip</button>
            {!step.waitForAction && stepIdx === STEPS.length - 1 && (
              <button className="tutorial-next" onClick={advance}>{step.actionLabel || 'Finish'}</button>
            )}
            {step.actionLabel && !step.waitForAction && stepIdx < STEPS.length - 1 && (
              <button className="tutorial-next" onClick={advance}>{step.actionLabel}</button>
            )}
            {!step.waitForAction && stepIdx < STEPS.length - 1 && (
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', alignSelf: 'center' }}>tap anywhere to continue</span>
            )}
          </div>

          <div className="tutorial-counter">{stepIdx + 1} / {STEPS.length}</div>
        </div>
      </div>
    </>
  );
}
